<template>
  <div class="self-center flex flex-col">
    <h1 class="text-2xl text-center">{{ t('langua.step_verification.title') }}</h1>
    <!-- 1. Open profile -->
    <section class="mt-8 pl-10">
      <h2 class="text-xl flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 mr-4 -ml-10 -my-0.5 fill-green-500">
          <path d="M14 1.5V22h-2V3.704L7.5 4.91V2.839l5-1.339z" />
        </svg>
        {{ t('langua.step_verification.action_update_skin.title') }}
      </h2>
      <div class="flex">
        <p
          :class="['flex-grow w-0 mt-4 leading-normal text-gray-700', {'text-justify': langOptions.text_justify}]"
          v-html="t('langua.step_verification.action_update_skin.description')"
        />
      </div>
      <div class="mt-4">
        <mc-button type="primary" @click="goProfile">
          {{ t('langua.step_verification.open_button_label') }}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 ml-1 -my-1 fill-current">
            <path d="M10 6v2H5v11h11v-5h2v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6zm11-3v8h-2V6.413l-7.793 7.794-1.414-1.414L17.585 5H13V3h8z" />
          </svg>
        </mc-button>
      </div>
      <div class="mt-4 flex">
        <hint-box type="tip" :content="t('langua.step_verification.action_update_skin.hint', verificationData.url, `${verificationData.name} (${challengeId}).png`)" class="flex-grow w-0" />
      </div>
    </section>

    <!-- 2. Verify skin update -->
    <section class="mt-8 pl-10">
      <h2 class="text-xl flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 mr-4 -ml-10 -my-0.5 fill-green-500">
          <path d="M16 7.5a4 4 0 1 0-8 0H6a6 6 0 1 1 10.663 3.776l-7.32 8.723L18 20v2H6v-1.127l9.064-10.802A3.982 3.982 0 0 0 16 7.5z" />
        </svg>
        {{ t('langua.step_verification.action_check_result.title') }}
      </h2>
      <div class="flex">
        <p :class="['flex-grow w-0 mt-4 leading-normal text-gray-700', {'text-justify': langOptions.text_justify}]">
          {{ t('langua.step_verification.action_check_result.description') }}
        </p>
      </div>
      <div class="mt-4">
        <mc-button ref="$verifyButton" type="primary" :busy="isVerifying" @click="verify">{{ t('langua.step_verification.check_button_label.normal') }}</mc-button>
      </div>
      <div class="flex">
        <hint-box v-if="msg" :type="msg.type" :title="t(msg.title)" :content="t(msg.content)" class="flex-grow w-0 mt-4" />
      </div>
    </section>

    <!-- ... -->
    <p class="py-8 text-2xl flex justify-center space-x-4">
      <span class="text-gray-400">&middot;</span>
      <span class="text-gray-400">&middot;</span>
      <span class="text-gray-400">&middot;</span>
    </p>

    <!-- Restart button -->
    <section>
      <button class="mx-auto text-green-600 hover:text-green-500 transition duration-200 ease-in-out flex items-center" @click="$emit('step', 0)">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 h-6 mr-1 -my-1 fill-current" style="transform: rotate(-90deg) rotateX(180deg);">
          <path d="M18.537 19.567A9.961 9.961 0 0 1 12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10c0 2.136-.67 4.116-1.81 5.74L17 12h3a8 8 0 1 0-2.46 5.772l.997 1.795z" />
        </svg>
        {{ t('langua.step_verification.reset_button_label') }}
      </button>
    </section>
  </div>
</template>

<script>
  import {ref} from '@vue/composition-api'

  import McButton from '@/components/mc-button.vue'
  import HintBox from '@/components/hint-box.vue'
  import useMelonAuth from '@/composables/melon-auth'
  import useI18n from '@/composables/i18n'

  const VERIFY_COOLDOWN = 61000 // 61s

  export default {
    name: 'VerificationView',

    components: {
      HintBox,
      McButton,
    },

    setup (_, {emit}) {
      function goProfile () {
        // TODO: direct to corresponding language
        window.open(`https://my.minecraft.net/zh-hans/profile/skin/remote?url=${encodeURIComponent(verificationData.value.url)}${verificationData.value.skinModel === 'alex' ? '&model=slim' : ''}`)
      }

      let $verifyButton = ref(null)
      let {challengeId, verificationData, checkResult} = useMelonAuth()
      let isVerifying = ref(false)
      /** @type {{value: {type: String, title: String, content: String}}} */
      let msg = ref(null)

      async function verify () {
        isVerifying.value = true
        let extraCooldown
        try {
          await checkResult()
          emit('forward')
        } catch (e) {
          msg.value = {
            type: e.type ?? 'error',
            title: e.error,
            content: e.errorMessage,
          }
          extraCooldown = e.extra
        }
        isVerifying.value = false
        $verifyButton.value.cooldown(extraCooldown ?? VERIFY_COOLDOWN)
      }

      const {langOptions, t} = useI18n()

      return {
        langOptions,
        t,

        goProfile,

        VERIFY_COOLDOWN,
        $verifyButton,
        isVerifying,
        challengeId,
        verificationData,
        verify,

        msg,
      }
    },
  }
</script>
